import type { ReactNode } from "react";

export type FeatureKey =
  | "ai-wa" | "funnel" | "followup" | "schedule" | "reminder" | "metrics"
  | "analysis" | "broadcast" | "api" | "multi" | "meta" | "tags"
  | "notify" | "dashboard" | "chats" | "lead" | "agents" | "audio" | "media"
  | "mappin" | "stars" | "serp" | "keywords" | "napsync" | "gallery"
  | "qa" | "speed" | "responsive" | "convert" | "semantic" | "seotech"
  | "localpages" | "pillar" | "internal-links" | "authority" | "editorial"
  | "calltrack";

// Soft tonal background per card — keeps palette consistent while differentiating.
const tones: Record<FeatureKey, string> = {
  "ai-wa":    "from-[oklch(0.62_0.18_150/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  funnel:     "from-[oklch(0.62_0.22_258/0.22)] to-[oklch(0.55_0.18_300/0.18)]",
  followup:   "from-[oklch(0.65_0.18_50/0.22)] to-[oklch(0.55_0.20_15/0.18)]",
  schedule:   "from-[oklch(0.60_0.20_200/0.22)] to-[oklch(0.55_0.18_258/0.18)]",
  reminder:   "from-[oklch(0.68_0.18_85/0.22)] to-[oklch(0.55_0.20_50/0.18)]",
  metrics:    "from-[oklch(0.62_0.20_180/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  analysis:   "from-[oklch(0.60_0.22_300/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  broadcast:  "from-[oklch(0.62_0.22_25/0.22)] to-[oklch(0.55_0.20_330/0.18)]",
  api:        "from-[oklch(0.62_0.18_150/0.22)] to-[oklch(0.55_0.18_200/0.18)]",
  multi:      "from-[oklch(0.60_0.20_258/0.22)] to-[oklch(0.55_0.18_180/0.18)]",
  meta:       "from-[oklch(0.55_0.22_258/0.25)] to-[oklch(0.50_0.20_300/0.18)]",
  tags:       "from-[oklch(0.65_0.20_120/0.22)] to-[oklch(0.55_0.20_180/0.18)]",
  notify:     "from-[oklch(0.68_0.18_70/0.22)] to-[oklch(0.55_0.20_25/0.18)]",
  dashboard:  "from-[oklch(0.60_0.20_258/0.22)] to-[oklch(0.55_0.18_220/0.18)]",
  chats:      "from-[oklch(0.62_0.18_150/0.22)] to-[oklch(0.55_0.18_180/0.18)]",
  lead:       "from-[oklch(0.60_0.20_300/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  agents:     "from-[oklch(0.60_0.22_280/0.25)] to-[oklch(0.55_0.20_200/0.18)]",
  audio:      "from-[oklch(0.60_0.22_330/0.22)] to-[oklch(0.55_0.20_280/0.18)]",
  media:      "from-[oklch(0.62_0.20_200/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  mappin:          "from-[oklch(0.62_0.22_25/0.22)] to-[oklch(0.55_0.20_50/0.18)]",
  stars:           "from-[oklch(0.72_0.18_85/0.25)] to-[oklch(0.55_0.20_50/0.18)]",
  serp:            "from-[oklch(0.60_0.20_258/0.22)] to-[oklch(0.55_0.18_220/0.18)]",
  keywords:        "from-[oklch(0.62_0.20_180/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  napsync:         "from-[oklch(0.62_0.18_150/0.22)] to-[oklch(0.55_0.18_200/0.18)]",
  gallery:         "from-[oklch(0.65_0.18_300/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  qa:              "from-[oklch(0.62_0.18_258/0.22)] to-[oklch(0.55_0.20_300/0.18)]",
  speed:           "from-[oklch(0.65_0.20_140/0.22)] to-[oklch(0.55_0.20_180/0.18)]",
  responsive:      "from-[oklch(0.60_0.20_258/0.22)] to-[oklch(0.55_0.18_200/0.18)]",
  convert:         "from-[oklch(0.62_0.20_150/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  semantic:        "from-[oklch(0.60_0.22_300/0.22)] to-[oklch(0.55_0.20_258/0.18)]",
  seotech:         "from-[oklch(0.60_0.20_220/0.22)] to-[oklch(0.55_0.18_258/0.18)]",
  localpages:      "from-[oklch(0.62_0.20_50/0.22)] to-[oklch(0.55_0.20_25/0.18)]",
  pillar:          "from-[oklch(0.60_0.22_258/0.22)] to-[oklch(0.55_0.20_300/0.18)]",
  "internal-links":"from-[oklch(0.60_0.20_200/0.22)] to-[oklch(0.55_0.18_258/0.18)]",
  authority:       "from-[oklch(0.62_0.20_50/0.22)] to-[oklch(0.55_0.20_85/0.18)]",
  editorial:       "from-[oklch(0.62_0.20_300/0.22)] to-[oklch(0.55_0.18_258/0.18)]",
  calltrack:       "from-[oklch(0.62_0.18_150/0.22)] to-[oklch(0.55_0.18_180/0.18)]",
};

function Scene({ k }: { k: FeatureKey }): ReactNode {
  const stroke = "oklch(0.95 0 0)";
  const accent = "oklch(0.78 0.18 150)"; // whatsapp green-ish
  const primary = "oklch(0.72 0.18 258)";
  switch (k) {
    case "ai-wa":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="20" y="22" width="110" height="46" rx="14" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.25" />
          <circle cx="42" cy="45" r="5" fill={accent} />
          <rect x="54" y="40" width="60" height="4" rx="2" fill={stroke} fillOpacity="0.6" />
          <rect x="54" y="50" width="42" height="4" rx="2" fill={stroke} fillOpacity="0.35" />
          <path d="M40 68 l-6 10 12 -6 z" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.25" />
          <g transform="translate(140 28)">
            <rect width="44" height="44" rx="12" fill={primary} fillOpacity="0.25" stroke={primary} />
            <text x="22" y="28" textAnchor="middle" fontFamily="ui-sans-serif" fontWeight="700" fontSize="14" fill={stroke}>IA</text>
            <circle cx="38" cy="8" r="4" fill={accent}><animate attributeName="opacity" values="1;0.3;1" dur="1.6s" repeatCount="indefinite" /></circle>
          </g>
        </svg>
      );
    case "funnel":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[0,1,2,3].map((i)=>(
            <g key={i} transform={`translate(${10 + i*46} 18)`}>
              <rect width="40" height="64" rx="8" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.2" />
              <rect x="6" y="8" width="28" height="6" rx="2" fill={primary} fillOpacity={0.4 + i*0.15} />
              <rect x="6" y="20" width="28" height="10" rx="2" fill={stroke} fillOpacity="0.18" />
              <rect x="6" y="34" width="28" height="10" rx="2" fill={stroke} fillOpacity="0.18" />
              <rect x="6" y="48" width="20" height="10" rx="2" fill={stroke} fillOpacity="0.18" />
            </g>
          ))}
          <circle cx="40" cy="56" r="4" fill={accent}>
            <animate attributeName="cx" values="40;86;132;178" dur="3.2s" repeatCount="indefinite" />
          </circle>
        </svg>
      );
    case "followup":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <circle cx="100" cy="50" r="30" fill="none" stroke={stroke} strokeOpacity="0.25" strokeDasharray="4 4" />
          <path d="M100 20 a30 30 0 0 1 26 15" fill="none" stroke={accent} strokeWidth="3" strokeLinecap="round" />
          <path d="M126 35 l4 -8 -10 -2 z" fill={accent} />
          <g><circle cx="100" cy="50" r="14" fill={primary} fillOpacity="0.25" stroke={primary} /><text x="100" y="55" textAnchor="middle" fontSize="14" fontWeight="700" fill={stroke}>↻</text></g>
          {[1,2,3].map(i=>(<rect key={i} x={30 + (i-1)*8} y={86} width="4" height="4" rx="1" fill={stroke} fillOpacity={0.2 + i*0.2} />))}
          {[1,2,3].map(i=>(<rect key={`r${i}`} x={158 + (i-1)*8} y={86} width="4" height="4" rx="1" fill={accent} fillOpacity={0.2 + i*0.2} />))}
        </svg>
      );
    case "schedule":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="40" y="18" width="120" height="68" rx="10" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.25" />
          <rect x="40" y="18" width="120" height="14" rx="10" fill={primary} fillOpacity="0.35" />
          {Array.from({length:21}).map((_,i)=>{
            const c = i%7, r = Math.floor(i/7);
            const sel = i===10;
            return <rect key={i} x={48 + c*16} y={40 + r*14} width="12" height="10" rx="2" fill={sel ? accent : "oklch(0.20 0.03 260 / 0.1)"} stroke={sel?accent:stroke} strokeOpacity={sel?1:0.2} />;
          })}
          <path d="M96 54 l4 4 8 -8" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "reminder":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(60 18)">
            <path d="M40 8 a26 26 0 0 1 26 26 v22 l8 8 h-68 l8 -8 v-22 a26 26 0 0 1 26 -26z" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.3" />
            <circle cx="40" cy="72" r="6" fill={accent} />
            <circle cx="62" cy="14" r="6" fill={accent}><animate attributeName="r" values="6;9;6" dur="1.4s" repeatCount="indefinite" /></circle>
          </g>
          <g transform="translate(20 70)" fill={stroke} fillOpacity="0.6" fontSize="10" fontFamily="ui-sans-serif">
            <text>15:00 ✓</text>
          </g>
        </svg>
      );
    case "metrics":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <line x1="20" y1="82" x2="180" y2="82" stroke={stroke} strokeOpacity="0.25" />
          {[28,46,32,58,40,70,52].map((h,i)=>(
            <rect key={i} x={28 + i*22} y={82-h} width="14" height={h} rx="3" fill={i===5?accent:primary} fillOpacity={i===5?0.9:0.5}>
              <animate attributeName="height" values={`0;${h}`} dur="0.9s" fill="freeze" />
              <animate attributeName="y" values={`82;${82-h}`} dur="0.9s" fill="freeze" />
            </rect>
          ))}
          <path d="M28 60 L50 50 L72 56 L94 42 L116 48 L138 30 L160 38" fill="none" stroke={stroke} strokeOpacity="0.6" strokeWidth="2" />
        </svg>
      );
    case "analysis":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="20" y="18" width="120" height="64" rx="10" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.25" />
          <rect x="32" y="30" width="80" height="4" rx="2" fill={stroke} fillOpacity="0.4" />
          <rect x="32" y="40" width="60" height="4" rx="2" fill={stroke} fillOpacity="0.3" />
          <rect x="32" y="50" width="90" height="4" rx="2" fill={stroke} fillOpacity="0.3" />
          <rect x="32" y="60" width="50" height="4" rx="2" fill={stroke} fillOpacity="0.3" />
          <g transform="translate(125 38)">
            <circle r="22" fill="none" stroke={accent} strokeWidth="3" />
            <line x1="16" y1="16" x2="32" y2="32" stroke={accent} strokeWidth="4" strokeLinecap="round" />
            <text textAnchor="middle" y="4" fontSize="11" fontWeight="700" fill={accent}>AI</text>
          </g>
        </svg>
      );
    case "broadcast":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(28 30)">
            <path d="M0 22 L36 6 L36 38 z" fill={primary} fillOpacity="0.5" stroke={primary} />
            <rect x="0" y="14" width="6" height="16" fill={primary} />
          </g>
          {[0,1,2,3,4].map(i=>(
            <g key={i} transform={`translate(${88 + (i%3)*32} ${20 + Math.floor(i/3)*36})`}>
              <rect width="26" height="20" rx="4" fill="oklch(0.20 0.03 260 / 0.1)" stroke={stroke} strokeOpacity="0.3" />
              <circle cx="6" cy="10" r="3" fill={accent} />
              <rect x="12" y="7" width="10" height="2" rx="1" fill={stroke} fillOpacity="0.5" />
              <rect x="12" y="12" width="8" height="2" rx="1" fill={stroke} fillOpacity="0.3" />
            </g>
          ))}
          <path d="M68 50 L86 30 M68 50 L86 50 M68 50 L86 70" stroke={accent} strokeWidth="2" strokeOpacity="0.7" />
        </svg>
      );
    case "api":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(70 14)">
            <path d="M30 0 L60 12 v22 c0 22 -15 36 -30 42 c-15 -6 -30 -20 -30 -42 v-22 z" fill="oklch(0.20 0.03 260 / 0.08)" stroke={accent} strokeWidth="2" />
            <path d="M18 38 l10 10 18 -22" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
          </g>
          <text x="100" y="92" textAnchor="middle" fontSize="9" fill={stroke} fillOpacity="0.6" fontFamily="ui-monospace">Meta Cloud API</text>
        </svg>
      );
    case "multi":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[0,1,2].map(i=>(
            <g key={i} transform={`translate(${60 + i*22} ${14 + i*8})`}>
              <rect width="48" height="72" rx="8" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.35" />
              <rect x="6" y="10" width="36" height="6" rx="2" fill={accent} fillOpacity={0.4 + i*0.2} />
              <rect x="6" y="22" width="36" height="4" rx="2" fill={stroke} fillOpacity="0.25" />
              <rect x="6" y="30" width="28" height="4" rx="2" fill={stroke} fillOpacity="0.25" />
              <circle cx="24" cy="58" r="6" fill={primary} fillOpacity="0.4" />
            </g>
          ))}
        </svg>
      );
    case "meta":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(30 26)">
            <path d="M0 24 C0 8 16 0 30 12 C44 24 58 40 70 28 C82 16 70 4 60 12" fill="none" stroke={primary} strokeWidth="6" strokeLinecap="round" />
            <path d="M0 24 C0 40 16 48 30 36" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
          </g>
          <g transform="translate(130 22)">
            <rect width="48" height="56" rx="10" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.3" />
            <text x="24" y="22" textAnchor="middle" fontSize="9" fill={stroke} fillOpacity="0.6">Leads</text>
            <text x="24" y="40" textAnchor="middle" fontSize="16" fontWeight="700" fill={accent}>+248</text>
          </g>
          <path d="M100 50 L126 50" stroke={stroke} strokeOpacity="0.4" strokeWidth="2" strokeDasharray="3 3" />
        </svg>
      );
    case "tags":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[
            {x:20,y:24,w:60,c:accent,l:"Quente"},
            {x:90,y:24,w:54,c:primary,l:"Novo"},
            {x:30,y:50,w:70,c:"oklch(0.7 0.18 50)",l:"Retorno"},
            {x:110,y:50,w:56,c:"oklch(0.7 0.18 300)",l:"VIP"},
            {x:50,y:76,w:80,c:"oklch(0.7 0.18 200)",l:"Agendado"},
          ].map((t,i)=>(
            <g key={i} transform={`translate(${t.x} ${t.y})`}>
              <path d={`M0 8 L8 0 H${t.w-4} a4 4 0 0 1 4 4 v8 a4 4 0 0 1 -4 4 H8 z`} fill={t.c} fillOpacity="0.25" stroke={t.c} />
              <circle cx="8" cy="8" r="2" fill={t.c} />
              <text x={(t.w+8)/2} y="12" textAnchor="middle" fontSize="8" fontWeight="600" fill={stroke}>{t.l}</text>
            </g>
          ))}
        </svg>
      );
    case "notify":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(20 24)">
            <path d="M30 0 a22 22 0 0 1 22 22 v20 l6 6 h-56 l6 -6 v-20 a22 22 0 0 1 22 -22z" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.3" />
            <circle cx="52" cy="4" r="6" fill={accent}><animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" /></circle>
          </g>
          {[0,1,2].map(i=>(
            <g key={i} transform={`translate(${100} ${20 + i*22})`}>
              <rect width="80" height="16" rx="6" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.25" />
              <circle cx="10" cy="8" r="3" fill={accent} />
              <rect x="18" y="6" width="50" height="3" rx="1" fill={stroke} fillOpacity="0.5" />
              <rect x="18" y="11" width="36" height="2" rx="1" fill={stroke} fillOpacity="0.3" />
            </g>
          ))}
        </svg>
      );
    case "dashboard":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="14" y="14" width="172" height="72" rx="8" fill="oklch(0.20 0.03 260 / 0.04)" stroke={stroke} strokeOpacity="0.2" />
          <rect x="22" y="22" width="50" height="28" rx="4" fill={primary} fillOpacity="0.3" />
          <text x="47" y="40" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>R$ 87k</text>
          <rect x="78" y="22" width="50" height="28" rx="4" fill={accent} fillOpacity="0.25" />
          <text x="103" y="40" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>312</text>
          <rect x="134" y="22" width="50" height="28" rx="4" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.2" />
          <text x="159" y="40" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>42%</text>
          <polyline points="22,76 50,66 78,72 106,58 134,62 162,48 184,54" fill="none" stroke={accent} strokeWidth="2" />
        </svg>
      );
    case "chats":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="14" y="14" width="70" height="72" rx="6" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.2" />
          {[0,1,2,3].map(i=>(
            <g key={i} transform={`translate(20 ${20 + i*16})`}>
              <circle cx="6" cy="6" r="5" fill={i===1?accent:primary} fillOpacity="0.5" />
              <rect x="16" y="2" width="40" height="3" rx="1" fill={stroke} fillOpacity="0.5" />
              <rect x="16" y="8" width="30" height="2" rx="1" fill={stroke} fillOpacity="0.3" />
            </g>
          ))}
          <rect x="90" y="14" width="96" height="72" rx="6" fill="oklch(0.20 0.03 260 / 0.04)" stroke={stroke} strokeOpacity="0.2" />
          <rect x="98" y="24" width="50" height="14" rx="7" fill={stroke} fillOpacity="0.15" />
          <rect x="120" y="44" width="58" height="14" rx="7" fill={accent} fillOpacity="0.4" />
          <rect x="98" y="64" width="40" height="14" rx="7" fill={stroke} fillOpacity="0.15" />
        </svg>
      );
    case "lead":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="30" y="14" width="140" height="72" rx="10" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.25" />
          <circle cx="58" cy="40" r="14" fill={primary} fillOpacity="0.4" stroke={primary} />
          <rect x="80" y="30" width="76" height="5" rx="2" fill={stroke} fillOpacity="0.6" />
          <rect x="80" y="40" width="60" height="3" rx="1" fill={stroke} fillOpacity="0.35" />
          <rect x="40" y="62" width="120" height="3" rx="1" fill={stroke} fillOpacity="0.25" />
          <rect x="40" y="70" width="90" height="3" rx="1" fill={stroke} fillOpacity="0.25" />
          <circle cx="156" cy="26" r="6" fill={accent} />
        </svg>
      );
    case "agents":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[0,1,2].map(i=>(
            <g key={i} transform={`translate(${30 + i*52} 18)`}>
              <rect width="44" height="64" rx="10" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.25" />
              <rect x="14" y="6" width="16" height="14" rx="4" fill={primary} fillOpacity="0.5" />
              <circle cx="22" cy="34" r="12" fill={primary} fillOpacity="0.3" stroke={primary} />
              <circle cx="18" cy="34" r="2" fill={stroke} />
              <circle cx="26" cy="34" r="2" fill={stroke} />
              <path d="M16 40 q6 4 12 0" stroke={stroke} fill="none" />
              <rect x="10" y="52" width="24" height="3" rx="1" fill={accent} fillOpacity={0.4 + i*0.2} />
            </g>
          ))}
        </svg>
      );
    case "audio":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="14" y="34" width="172" height="32" rx="16" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.25" />
          <circle cx="30" cy="50" r="9" fill={accent} />
          <path d="M27 46 l8 4 -8 4 z" fill={stroke} />
          {Array.from({length:22}).map((_,i)=>{
            const h = 4 + Math.abs(Math.sin(i*0.9))*16;
            return <rect key={i} x={50 + i*6} y={50 - h/2} width="3" height={h} rx="1.5" fill={i<12?accent:stroke} fillOpacity={i<12?0.9:0.35} />;
          })}
        </svg>
      );
    case "media":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(40 22)">
            <rect width="56" height="56" rx="8" fill={primary} fillOpacity="0.25" stroke={primary} />
            <circle cx="16" cy="18" r="5" fill={stroke} fillOpacity="0.7" />
            <path d="M6 46 L22 30 L36 42 L48 32 L52 46 z" fill={stroke} fillOpacity="0.4" />
          </g>
          <g transform="translate(78 32)">
            <rect width="56" height="56" rx="8" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.3" />
            <circle cx="28" cy="28" r="12" fill={accent} fillOpacity="0.3" stroke={accent} />
            <path d="M24 22 l10 6 -10 6 z" fill={accent} />
          </g>
          <g transform="translate(116 22)">
            <rect width="56" height="56" rx="8" fill={accent} fillOpacity="0.15" stroke={accent} strokeOpacity="0.6" />
            <rect x="10" y="14" width="36" height="3" rx="1" fill={stroke} fillOpacity="0.5" />
            <rect x="10" y="22" width="28" height="3" rx="1" fill={stroke} fillOpacity="0.4" />
            <rect x="10" y="34" width="22" height="8" rx="2" fill={accent} fillOpacity="0.6" />
          </g>
        </svg>
      );
    case "mappin":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="10" y="10" width="180" height="80" rx="10" fill="oklch(0.55 0.05 220 / 0.18)" stroke={stroke} strokeOpacity="0.2" />
          <path d="M0 70 L200 40" stroke={stroke} strokeOpacity="0.25" strokeWidth="2" />
          <path d="M60 10 L80 90" stroke={stroke} strokeOpacity="0.2" strokeWidth="2" />
          <path d="M140 10 L160 90" stroke={stroke} strokeOpacity="0.2" strokeWidth="2" />
          <circle cx="50" cy="60" r="5" fill={stroke} fillOpacity="0.35" />
          <circle cx="160" cy="34" r="5" fill={stroke} fillOpacity="0.35" />
          <g transform="translate(92 18)">
            <path d="M16 0 a16 16 0 0 1 16 16 c0 14 -16 30 -16 30 S0 30 0 16 A16 16 0 0 1 16 0z" fill={accent} stroke={stroke} strokeOpacity="0.4" />
            <circle cx="16" cy="16" r="6" fill={stroke} />
            <circle cx="16" cy="50" r="6" fill={accent} fillOpacity="0.25">
              <animate attributeName="r" values="6;14;6" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
            </circle>
          </g>
        </svg>
      );
    case "stars":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(20 30)">
            {[0,1,2,3,4].map(i=>(
              <path key={i} d={`M${i*32+14} 0 l4 12 12 0 -10 8 4 12 -10 -8 -10 8 4 -12 -10 -8 12 0 z`} fill={accent} fillOpacity={i<4?0.95:0.4} stroke={accent} />
            ))}
          </g>
          <text x="100" y="78" textAnchor="middle" fontSize="18" fontWeight="700" fill={stroke}>4,9</text>
          <text x="138" y="78" fontSize="9" fill={stroke} fillOpacity="0.55">(248 avaliações)</text>
        </svg>
      );
    case "serp":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="14" y="12" width="172" height="76" rx="8" fill="oklch(0.20 0.03 260 / 0.05)" stroke={stroke} strokeOpacity="0.2" />
          <rect x="22" y="20" width="156" height="12" rx="6" fill="oklch(0.20 0.03 260 / 0.08)" stroke={stroke} strokeOpacity="0.25" />
          <circle cx="30" cy="26" r="3" fill={stroke} fillOpacity="0.5" />
          {[0,1,2].map(i=>(
            <g key={i} transform={`translate(22 ${40 + i*16})`}>
              <rect width="10" height="10" rx="2" fill={i===0?accent:primary} fillOpacity={i===0?0.9:0.35} />
              <text x="4" y="8" fontSize="7" fontWeight="700" fill={stroke}>{i+1}</text>
              <rect x="16" y="1" width="120" height="3" rx="1" fill={stroke} fillOpacity={i===0?0.7:0.35} />
              <rect x="16" y="6" width="90" height="2" rx="1" fill={stroke} fillOpacity="0.25" />
            </g>
          ))}
        </svg>
      );
    case "keywords":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[
            {x:14,y:14,w:62,c:accent,l:"#ficha google"},
            {x:84,y:14,w:54,c:primary,l:"#seo local"},
            {x:146,y:14,w:44,c:"oklch(0.7 0.18 300)",l:"#maps"},
            {x:24,y:42,w:70,c:"oklch(0.72 0.18 50)",l:"#mais clientes"},
            {x:104,y:42,w:80,c:"oklch(0.7 0.18 200)",l:"#palavra-chave"},
            {x:40,y:70,w:60,c:primary,l:"#ranking"},
            {x:110,y:70,w:60,c:accent,l:"#busca"},
          ].map((t,i)=>(
            <g key={i} transform={`translate(${t.x} ${t.y})`}>
              <rect width={t.w} height="18" rx="9" fill={t.c} fillOpacity="0.2" stroke={t.c} strokeOpacity="0.7" />
              <text x={t.w/2} y="12" textAnchor="middle" fontSize="8" fontWeight="600" fill={stroke}>{t.l}</text>
            </g>
          ))}
        </svg>
      );
    case "napsync":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[{y:14,l:"Nome",v:"SVG Multimídia"},{y:40,l:"Endereço",v:"Rua Tal, 123"},{y:66,l:"Telefone",v:"(55) 9685-2045"}].map((r,i)=>(
            <g key={i} transform={`translate(14 ${r.y})`}>
              <rect width="172" height="20" rx="6" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.2" />
              <text x="10" y="13" fontSize="8" fill={stroke} fillOpacity="0.5">{r.l}</text>
              <text x="56" y="13" fontSize="9" fontWeight="600" fill={stroke}>{r.v}</text>
              <g transform="translate(150 4)">
                <circle r="6" cx="6" cy="6" fill={accent} fillOpacity="0.25" stroke={accent} />
                <path d="M3 6 l2 2 4 -4" fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
              </g>
            </g>
          ))}
        </svg>
      );
    case "gallery":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[
            {x:14,y:14,c:primary},{x:74,y:14,c:accent},{x:134,y:14,c:"oklch(0.7 0.18 300)"},
            {x:14,y:54,c:"oklch(0.7 0.18 50)"},{x:74,y:54,c:primary},{x:134,y:54,c:accent},
          ].map((t,i)=>(
            <g key={i} transform={`translate(${t.x} ${t.y})`}>
              <rect width="52" height="34" rx="4" fill={t.c} fillOpacity="0.18" stroke={t.c} strokeOpacity="0.6" />
              <circle cx="14" cy="12" r="3" fill={stroke} fillOpacity="0.6" />
              <path d="M6 30 L18 18 L28 26 L40 16 L46 30 z" fill={stroke} fillOpacity="0.35" />
            </g>
          ))}
        </svg>
      );
    case "qa":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(14 14)">
            <rect width="100" height="26" rx="12" fill={primary} fillOpacity="0.25" stroke={primary} strokeOpacity="0.7" />
            <text x="10" y="17" fontSize="10" fontWeight="700" fill={stroke}>?</text>
            <rect x="22" y="9" width="70" height="3" rx="1" fill={stroke} fillOpacity="0.55" />
            <rect x="22" y="16" width="50" height="3" rx="1" fill={stroke} fillOpacity="0.3" />
          </g>
          <g transform="translate(70 50)">
            <rect width="116" height="34" rx="12" fill={accent} fillOpacity="0.22" stroke={accent} strokeOpacity="0.7" />
            <text x="106" y="20" fontSize="10" fontWeight="700" fill={stroke}>!</text>
            <rect x="10" y="10" width="86" height="3" rx="1" fill={stroke} fillOpacity="0.6" />
            <rect x="10" y="18" width="70" height="3" rx="1" fill={stroke} fillOpacity="0.4" />
            <rect x="10" y="25" width="56" height="3" rx="1" fill={stroke} fillOpacity="0.4" />
          </g>
        </svg>
      );
    case "speed":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {/* Gauge style PageSpeed: anel completo com score 98 */}
          {(() => {
            const cx = 44, cy = 50, r = 30;
            const C = 2 * Math.PI * r;
            const score = 98;
            const dash = (score / 100) * C;
            const good = "oklch(0.82 0.16 150)"; // verde
            return (
              <g>
                <circle cx={cx} cy={cy} r={r} fill="none" stroke={stroke} strokeOpacity="0.18" strokeWidth="6" />
                <circle
                  cx={cx} cy={cy} r={r}
                  fill="none" stroke={good} strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={`${dash} ${C - dash}`}
                  transform={`rotate(-90 ${cx} ${cy})`}
                />
                <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fontSize="18" fontWeight="800" fill={good}>98</text>
                <text x={cx} y={cy + 13} textAnchor="middle" fontSize="6" fontWeight="600" fill={stroke} fillOpacity="0.55" letterSpacing="0.5">PERFORMANCE</text>
              </g>
            );
          })()}

          {/* Três métricas Core Web Vitals com barras de faixa (good/needs/poor) */}
          {(() => {
            const good = "oklch(0.82 0.16 150)";
            const warn = "oklch(0.82 0.16 80)";
            const bad = "oklch(0.7 0.2 25)";
            const metrics = [
              { label: "LCP", value: "1.2s", pos: 0.18, color: good },
              { label: "CLS", value: "0.02", pos: 0.12, color: good },
              { label: "INP", value: "84ms", pos: 0.22, color: good },
            ];
            const x0 = 92;
            const barW = 88;
            return metrics.map((m, i) => {
              const y = 18 + i * 24;
              return (
                <g key={m.label}>
                  <text x={x0} y={y + 4} fontSize="7.5" fontWeight="700" fill={stroke} fillOpacity="0.85">{m.label}</text>
                  <text x={x0 + barW} y={y + 4} textAnchor="end" fontSize="7.5" fontWeight="700" fill={m.color}>{m.value}</text>
                  {/* faixas */}
                  <rect x={x0} y={y + 8} width={barW * 0.55} height="3" rx="1.5" fill={good} fillOpacity="0.35" />
                  <rect x={x0 + barW * 0.55} y={y + 8} width={barW * 0.25} height="3" rx="1.5" fill={warn} fillOpacity="0.35" />
                  <rect x={x0 + barW * 0.8} y={y + 8} width={barW * 0.2} height="3" rx="1.5" fill={bad} fillOpacity="0.35" />
                  {/* marcador */}
                  <g transform={`translate(${x0 + barW * m.pos} ${y + 9.5})`}>
                    <circle r="3" fill={m.color} />
                    <circle r="3" fill="none" stroke={m.color} strokeOpacity="0.4" strokeWidth="1.2">
                      <animate attributeName="r" values="3;6;3" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="2s" repeatCount="indefinite" />
                    </circle>
                  </g>
                </g>
              );
            });
          })()}
        </svg>
      );
    case "responsive":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(20 18)">
            <rect width="92" height="56" rx="4" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.3" />
            <rect x="6" y="6" width="80" height="6" rx="2" fill={primary} fillOpacity="0.4" />
            <rect x="6" y="18" width="40" height="4" rx="1" fill={stroke} fillOpacity="0.3" />
            <rect x="6" y="26" width="60" height="4" rx="1" fill={stroke} fillOpacity="0.25" />
            <rect x="6" y="40" width="30" height="10" rx="2" fill={accent} fillOpacity="0.6" />
          </g>
          <g transform="translate(122 22)">
            <rect width="36" height="50" rx="4" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.3" />
            <rect x="4" y="4" width="28" height="4" rx="1" fill={primary} fillOpacity="0.4" />
            <rect x="4" y="36" width="20" height="8" rx="2" fill={accent} fillOpacity="0.6" />
          </g>
          <g transform="translate(166 26)">
            <rect width="20" height="44" rx="3" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.3" />
            <rect x="3" y="4" width="14" height="3" rx="1" fill={primary} fillOpacity="0.45" />
            <rect x="3" y="32" width="14" height="6" rx="2" fill={accent} fillOpacity="0.65" />
          </g>
        </svg>
      );
    case "convert":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <defs>
            <linearGradient id="cvF1" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor={primary} stopOpacity="0.55" />
              <stop offset="1" stopColor={primary} stopOpacity="0.25" />
            </linearGradient>
            <linearGradient id="cvF2" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor={primary} stopOpacity="0.5" />
              <stop offset="1" stopColor={accent} stopOpacity="0.35" />
            </linearGradient>
            <linearGradient id="cvF3" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor={accent} stopOpacity="0.6" />
              <stop offset="1" stopColor={accent} stopOpacity="0.95" />
            </linearGradient>
          </defs>
          {/* Funnel stages */}
          {[
            { y: 14, h: 18, x1: 14, x2: 186, label: "Visitas", value: "12.480", fill: "url(#cvF1)", stroke: primary },
            { y: 38, h: 18, x1: 34, x2: 166, label: "Leads",   value: "3.120",  fill: "url(#cvF2)", stroke: primary },
            { y: 62, h: 18, x1: 60, x2: 140, label: "Vendas",  value: "742",    fill: "url(#cvF3)", stroke: accent  },
          ].map((s, i) => (
            <g key={i}>
              <path
                d={`M${s.x1} ${s.y} L${s.x2} ${s.y} L${s.x2 - 8} ${s.y + s.h} L${s.x1 + 8} ${s.y + s.h} z`}
                fill={s.fill}
                stroke={s.stroke}
                strokeOpacity="0.55"
              />
              <text x={(s.x1 + s.x2) / 2} y={s.y + 11} textAnchor="middle" fontSize="7.5" fontWeight="700" fill={stroke} fillOpacity="0.85">{s.label}</text>
              <text x={(s.x1 + s.x2) / 2} y={s.y + s.h - 3} textAnchor="middle" fontSize="6.5" fontWeight="600" fill={stroke} fillOpacity="0.55">{s.value}</text>
            </g>
          ))}
          {/* Conversion deltas on the right */}
          <g fontSize="6" fontWeight="700" fontFamily="ui-monospace">
            <text x="192" y="38" textAnchor="end" fill={accent}>25%</text>
            <text x="192" y="62" textAnchor="end" fill={accent}>23,8%</text>
          </g>
          {/* Bottom KPI strip */}
          <g transform="translate(14 88)">
            <rect width="172" height="10" rx="3" fill="oklch(0.20 0.03 260 / 0.05)" stroke={stroke} strokeOpacity="0.18" />
            <circle cx="8" cy="5" r="2.5" fill={accent} />
            <text x="16" y="7.5" fontSize="6.5" fontWeight="700" fill={stroke} fillOpacity="0.85">CR 5,94%</text>
            <text x="78" y="7.5" fontSize="6.5" fontWeight="600" fill={stroke} fillOpacity="0.6">Ticket R$ 487</text>
            <text x="162" y="7.5" textAnchor="end" fontSize="6.5" fontWeight="700" fill={accent}>+38%</text>
          </g>
        </svg>
      );
    case "semantic":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="14" y="14" width="172" height="72" rx="8" fill="oklch(0.20 0.03 260 / 0.05)" stroke={stroke} strokeOpacity="0.2" />
          <g fontFamily="ui-monospace" fontSize="9">
            <text x="24" y="30" fill={accent}>&lt;h1&gt;</text>
            <rect x="52" y="22" width="90" height="8" rx="2" fill={stroke} fillOpacity="0.5" />
            <text x="24" y="48" fill={primary}>&lt;h2&gt;</text>
            <rect x="52" y="40" width="64" height="6" rx="2" fill={stroke} fillOpacity="0.35" />
            <text x="24" y="64" fill={primary}>&lt;p&gt;</text>
            <rect x="48" y="56" width="120" height="4" rx="1" fill={stroke} fillOpacity="0.25" />
            <rect x="48" y="64" width="100" height="4" rx="1" fill={stroke} fillOpacity="0.25" />
            <text x="24" y="80" fill={accent}>&lt;schema&gt;</text>
            <rect x="68" y="72" width="80" height="6" rx="2" fill={accent} fillOpacity="0.35" />
          </g>
        </svg>
      );
    case "seotech":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <circle cx="100" cy="20" r="9" fill={primary} fillOpacity="0.5" stroke={primary} />
          <line x1="100" y1="29" x2="40" y2="56" stroke={stroke} strokeOpacity="0.35" />
          <line x1="100" y1="29" x2="100" y2="56" stroke={stroke} strokeOpacity="0.35" />
          <line x1="100" y1="29" x2="160" y2="56" stroke={stroke} strokeOpacity="0.35" />
          {[40,100,160].map((x,i)=>(
            <g key={i}>
              <rect x={x-12} y="56" width="24" height="14" rx="3" fill={accent} fillOpacity="0.3" stroke={accent} strokeOpacity="0.7" />
              <line x1={x} y1="70" x2={x-12} y2="88" stroke={stroke} strokeOpacity="0.25" />
              <line x1={x} y1="70" x2={x+12} y2="88" stroke={stroke} strokeOpacity="0.25" />
            </g>
          ))}
          <text x="100" y="24" textAnchor="middle" fontSize="7" fontWeight="700" fill={stroke}>/</text>
        </svg>
      );
    case "localpages":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[
            {x:14,y:14,c:"Vila Mariana"},{x:74,y:14,c:"Centro"},{x:134,y:14,c:"Pinheiros"},
            {x:14,y:54,c:"Moema"},{x:74,y:54,c:"Itaim"},{x:134,y:54,c:"Tatuapé"},
          ].map((t,i)=>(
            <g key={i} transform={`translate(${t.x} ${t.y})`}>
              <rect width="52" height="32" rx="4" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.25" />
              <path d="M8 8 l3 -4 3 4 -3 4 z" fill={accent} />
              <text x="30" y="13" textAnchor="middle" fontSize="7" fontWeight="700" fill={stroke}>{t.c}</text>
              <rect x="6" y="20" width="40" height="3" rx="1" fill={stroke} fillOpacity="0.3" />
            </g>
          ))}
        </svg>
      );
    case "pillar":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <circle cx="100" cy="50" r="20" fill={primary} fillOpacity="0.35" stroke={primary} />
          <text x="100" y="54" textAnchor="middle" fontSize="9" fontWeight="700" fill={stroke}>Pilar</text>
          {[
            {x:30,y:22},{x:30,y:78},{x:170,y:22},{x:170,y:78},{x:100,y:10},{x:100,y:90},
          ].map((p,i)=>(
            <g key={i}>
              <line x1="100" y1="50" x2={p.x} y2={p.y} stroke={stroke} strokeOpacity="0.3" strokeDasharray="2 2" />
              <circle cx={p.x} cy={p.y} r="7" fill={accent} fillOpacity="0.35" stroke={accent} />
            </g>
          ))}
        </svg>
      );
    case "internal-links":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          {[{x:30,y:30},{x:100,y:18},{x:170,y:34},{x:50,y:74},{x:120,y:78},{x:160,y:62}].map((p,i)=>(
            <circle key={i} cx={p.x} cy={p.y} r="6" fill={i%2?accent:primary} fillOpacity="0.55" stroke={stroke} strokeOpacity="0.4" />
          ))}
          <g stroke={stroke} strokeOpacity="0.35" strokeWidth="1.2">
            <line x1="30" y1="30" x2="100" y2="18" />
            <line x1="100" y1="18" x2="170" y2="34" />
            <line x1="30" y1="30" x2="50" y2="74" />
            <line x1="100" y1="18" x2="120" y2="78" />
            <line x1="170" y1="34" x2="160" y2="62" />
            <line x1="50" y1="74" x2="120" y2="78" />
            <line x1="120" y1="78" x2="160" y2="62" />
          </g>
        </svg>
      );
    case "authority":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(78 14)">
            <path d="M22 0 L44 8 v22 c0 18 -12 32 -22 38 c-10 -6 -22 -20 -22 -38 v-22 z" fill={accent} fillOpacity="0.25" stroke={accent} strokeWidth="2" />
            <path d="M12 36 l8 8 14 -18" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
          </g>
          <text x="100" y="92" textAnchor="middle" fontSize="9" fontWeight="700" fill={stroke}>E-E-A-T · Autoridade</text>
        </svg>
      );
    case "editorial":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <rect x="14" y="14" width="80" height="72" rx="6" fill="oklch(0.20 0.03 260 / 0.05)" stroke={stroke} strokeOpacity="0.25" />
          <rect x="14" y="14" width="80" height="14" rx="6" fill={primary} fillOpacity="0.4" />
          {Array.from({length:12}).map((_,i)=>{
            const c=i%4,r=Math.floor(i/4);
            const sel=[1,6,10].includes(i);
            return <rect key={i} x={20+c*18} y={34+r*16} width="14" height="10" rx="2" fill={sel?accent:"oklch(0.20 0.03 260 / 0.1)"} stroke={sel?accent:stroke} strokeOpacity={sel?1:0.2} />
          })}
          {[0,1,2].map(i=>(
            <g key={i} transform={`translate(106 ${16+i*24})`}>
              <rect width="80" height="20" rx="4" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.2" />
              <rect x="6" y="4" width="10" height="12" rx="2" fill={accent} fillOpacity="0.5" />
              <rect x="20" y="6" width="54" height="3" rx="1" fill={stroke} fillOpacity="0.5" />
              <rect x="20" y="12" width="40" height="2" rx="1" fill={stroke} fillOpacity="0.3" />
            </g>
          ))}
        </svg>
      );
    case "calltrack":
      return (
        <svg viewBox="0 0 200 100" className="h-full w-full">
          <g transform="translate(20 26)">
            <path d="M4 0 h14 l6 14 -8 6 c4 8 10 14 18 18 l6 -8 14 6 v14 c0 4 -2 6 -6 6 C24 56 0 32 0 6 C0 2 2 0 4 0z" fill={accent} fillOpacity="0.3" stroke={accent} strokeWidth="2" />
          </g>
          <g transform="translate(100 22)">
            <rect width="80" height="56" rx="6" fill="oklch(0.20 0.03 260 / 0.06)" stroke={stroke} strokeOpacity="0.25" />
            <text x="10" y="18" fontSize="8" fill={stroke} fillOpacity="0.55">Ligações / mês</text>
            <text x="10" y="38" fontSize="18" fontWeight="700" fill={accent}>+184</text>
            <polyline points="10,50 22,46 34,48 46,40 58,42 70,32" fill="none" stroke={accent} strokeWidth="2" />
          </g>
        </svg>
      );
  }
}

export function FeatureVisual({ k }: { k: FeatureKey }) {
  return (
    <div className={`relative overflow-hidden bg-gradient-to-br ${tones[k]}`}>
      {/* base glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,oklch(1_0_0/0.10),transparent_60%)]" />
      {/* subtle grid */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(oklch(1_0_0)_1px,transparent_1px),linear-gradient(90deg,oklch(1_0_0)_1px,transparent_1px)] [background-size:22px_22px]" />
      {/* corner highlight */}
      <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-ink/[0.08] blur-2xl" />
      <div className="relative aspect-[2/1] w-full p-3 transition-transform duration-500 group-hover:scale-[1.03]">
        <Scene k={k} />
      </div>
    </div>
  );
}